import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
// @ts-ignore
import { getNewsBySlug, getNews } from "../../services/newsService";

interface NewsCategory {
  _id: string;
  name: string;
  slug: string;
}

interface NewsItem {
  _id: string;
  title: string;
  excerpt: string;
  content: string;
  newCategoryId?: NewsCategory;
  newCategory?: NewsCategory;
  author: string;
  readTime: string;
  tags: string[];
  createdAt: string;
  updatedAt: string;
  slug: string;
  image: string;
}

interface Comment {
  id: string;
  name: string;
  email: string;
  content: string;
  createdAt: string;
  replies: Comment[];
}

interface NewsDetailPageProps {
  slug?: string;
}

export default function NewsDetailPage({ slug: propSlug }: NewsDetailPageProps = {}) {
  const params = useParams<{ slug: string }>();
  // Use slug from props first, fallback to params
  const slug = propSlug || params.slug;
  
  const [isVisible, setIsVisible] = useState(false);
  const [relatedVisible, setRelatedVisible] = useState(false);
  const [commentsVisible, setCommentsVisible] = useState(false);

  // API states
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [article, setArticle] = useState<NewsItem | null>(null);
  const [relatedArticles, setRelatedArticles] = useState<NewsItem[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Check if slug exists
        if (!slug) {
          setError("Slug không hợp lệ");
          return;
        }

        // Fetch article by slug
        const articleResponse = await getNewsBySlug(slug);
        setArticle(articleResponse.data);

        // Fetch related articles
        const relatedResponse = await getNews();
        const allNews = relatedResponse.data?.news || [];
        
        // Loại bỏ bài hiện tại
        const otherNews = allNews.filter((news: NewsItem) => news._id !== articleResponse.data._id);
        
        // Ưu tiên bài cùng category
        const categoryId = articleResponse.data?.newCategory?._id || articleResponse.data?.newCategoryId?._id;
        const sameCategory = otherNews.filter((news: NewsItem) => 
          (news.newCategory?._id || news.newCategoryId?._id) === categoryId
        );
        
        // Nếu có bài cùng category thì lấy, không thì lấy bài mới nhất
        const related = sameCategory.length > 0 
          ? sameCategory.slice(0, 3) 
          : otherNews.slice(0, 3);
          
        setRelatedArticles(related);
      } catch (err: any) {
        setError(err.message || "Có lỗi xảy ra khi tải bài viết");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [slug]);

  // Comment states
  const [comments, setComments] = useState<Comment[]>([
    {
      id: "1",
      name: "Nguyễn Văn A",
      email: "nguyenvana@example.com",
      content:
        "Bài viết rất hữu ích! Tôi đã áp dụng các biện pháp bảo mật này cho dự án của mình và thấy hiệu quả rõ rệt.",
      createdAt: "2025-01-16T10:30:00.000Z",
      replies: [
        {
          id: "1-1",
          name: "Admin",
          email: "admin@example.com",
          content:
            "Cảm ơn bạn đã chia sẻ! Rất vui khi bài viết giúp ích được cho dự án của bạn.",
          createdAt: "2025-01-16T11:00:00.000Z",
          replies: [],
        },
      ],
    },
    {
      id: "2",
      name: "Trần Thị B",
      email: "tranthib@example.com",
      content:
        "Cho mình hỏi về phần JWT authentication, có nên sử dụng refresh token không ạ?",
      createdAt: "2025-01-16T14:20:00.000Z",
      replies: [
        {
          id: "2-1",
          name: "Hoàng Văn E",
          email: "hoangvane@example.com",
          content:
            "Có nên sử dụng refresh token để tăng tính bảo mật. Access token nên có thời gian sống ngắn (15-30 phút), còn refresh token có thể dài hơn (7-30 ngày).",
          createdAt: "2025-01-16T15:00:00.000Z",
          replies: [
            {
              id: "2-1-1",
              name: "Trần Thị B",
              email: "tranthib@example.com",
              content: "Cảm ơn bạn! Mình sẽ thử implement theo cách này.",
              createdAt: "2025-01-16T15:30:00.000Z",
              replies: [],
            },
          ],
        },
      ],
    },
    {
      id: "3",
      name: "Lê Minh C",
      email: "leminhc@example.com",
      content:
        "Bài viết rất chi tiết và dễ hiểu. Mong tác giả viết thêm về OWASP Top 10 ạ!",
      createdAt: "2025-01-17T09:15:00.000Z",
      replies: [],
    },
    {
      id: "4",
      name: "Phạm Thị D",
      email: "phamthid@example.com",
      content:
        "Mình đang làm dự án e-commerce, có thể áp dụng những biện pháp này không ạ?",
      createdAt: "2025-01-17T10:30:00.000Z",
      replies: [],
    },
    {
      id: "5",
      name: "Võ Văn E",
      email: "vovane@example.com",
      content:
        "Rất hữu ích! Cho mình hỏi về CORS policy nên config như thế nào cho đúng?",
      createdAt: "2025-01-17T11:45:00.000Z",
      replies: [],
    },
    {
      id: "6",
      name: "Đặng Thị F",
      email: "dangthif@example.com",
      content: "Cảm ơn tác giả! Bài viết giúp mình hiểu rõ hơn về bảo mật web.",
      createdAt: "2025-01-17T13:20:00.000Z",
      replies: [],
    },
  ]);

  const [newComment, setNewComment] = useState({
    name: "",
    email: "",
    content: "",
  });
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [replyContent, setReplyContent] = useState({
    name: "",
    email: "",
    content: "",
  });

  // Pagination states
  const [commentsPerPage] = useState(3);
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedReplies, setExpandedReplies] = useState<Set<string>>(
    new Set()
  );

  const currentNews = article;
  const category = currentNews?.newCategory || currentNews?.newCategoryId;
  const relatedNews = relatedArticles;

  useEffect(() => {
    setTimeout(() => {
      setIsVisible(true);
      setCommentsVisible(true);
      setRelatedVisible(true);
    }, 100);
  }, []);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#00c897] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Đang tải bài viết...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-6">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-error-warning-line text-4xl text-red-500"></i>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Có lỗi xảy ra</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <Link
            to="/news"
            className="inline-block px-6 py-3 bg-[#00c897] text-white font-semibold rounded-lg hover:bg-[#00b587] transition-colors"
          >
            Quay lại trang tin tức
          </Link>
        </div>
      </div>
    );
  }

  // Not found state
  if (!currentNews) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-6">
          <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-file-search-line text-4xl text-gray-400"></i>
          </div>
          <h1 className="text-3xl font-bold text-[#333] mb-4">
            Không tìm thấy bài viết
          </h1>
          <p className="text-gray-600 mb-6">
            Bài viết bạn đang tìm kiếm không tồn tại hoặc đã bị xóa.
          </p>
          <Link
            to="/news"
            className="inline-block px-6 py-3 bg-[#00c897] text-white font-semibold rounded-lg hover:bg-[#00b587] transition-colors"
          >
            Quay lại trang tin tức
          </Link>
        </div>
      </div>
    );
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("vi-VN", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatCommentDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInHours / 24);

    if (diffInHours < 1) return "Vừa xong";
    if (diffInHours < 24) return `${diffInHours} giờ trước`;
    if (diffInDays < 7) return `${diffInDays} ngày trước`;
    return date.toLocaleDateString("vi-VN", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const handleShare = (platform: string) => {
    const url = window.location.href;
    const title = currentNews.title;

    const shareUrls: { [key: string]: string } = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
        url
      )}`,
      twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(
        url
      )}&text=${encodeURIComponent(title)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
        url
      )}`,
    };

    if (platform === "copy") {
      navigator.clipboard.writeText(url);
      alert("Đã sao chép link!");
    } else {
      window.open(shareUrls[platform], "_blank", "width=600,height=400");
    }
  };

  const handleSubmitComment = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComment.name || !newComment.email || !newComment.content) return;

    const comment: Comment = {
      id: Date.now().toString(),
      name: newComment.name,
      email: newComment.email,
      content: newComment.content,
      createdAt: new Date().toISOString(),
      replies: [],
    };

    setComments([comment, ...comments]);
    setNewComment({ name: "", email: "", content: "" });
  };

  const handleSubmitReply = (parentId: string, e: React.FormEvent) => {
    e.preventDefault();
    if (!replyContent.name || !replyContent.email || !replyContent.content)
      return;

    const reply: Comment = {
      id: Date.now().toString(),
      name: replyContent.name,
      email: replyContent.email,
      content: replyContent.content,
      createdAt: new Date().toISOString(),
      replies: [],
    };

    const addReply = (comments: Comment[]): Comment[] => {
      return comments.map((comment) => {
        if (comment.id === parentId) {
          return { ...comment, replies: [...comment.replies, reply] };
        }
        if (comment.replies.length > 0) {
          return { ...comment, replies: addReply(comment.replies) };
        }
        return comment;
      });
    };

    setComments(addReply(comments));
    setReplyContent({ name: "", email: "", content: "" });
    setReplyingTo(null);
  };

  // Calculate pagination
  const indexOfLastComment = currentPage * commentsPerPage;
  const indexOfFirstComment = indexOfLastComment - commentsPerPage;
  const currentComments = comments.slice(
    indexOfFirstComment,
    indexOfLastComment
  );
  const totalPages = Math.ceil(comments.length / commentsPerPage);
  const hasMoreComments = currentPage < totalPages;

  const handleLoadMore = () => {
    setCurrentPage((prev) => prev + 1);
  };

  const toggleReplies = (commentId: string) => {
    setExpandedReplies((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(commentId)) {
        newSet.delete(commentId);
      } else {
        newSet.add(commentId);
      }
      return newSet;
    });
  };

  const renderComment = (comment: Comment, depth: number = 0) => {
    const isReplying = replyingTo === comment.id;
    const marginLeft = depth > 0 ? "ml-12" : "";
    const isExpanded = expandedReplies.has(comment.id);
    const hasReplies = comment.replies.length > 0;

    return (
      <div key={comment.id} className={`${marginLeft} mb-6`}>
        <div className="bg-white rounded-xl border-2 border-gray-100 p-6 hover:border-[#2a4b7c]/30 transition-all duration-300">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-[#2a4b7c] to-[#1e3a5f] rounded-full text-white font-bold text-lg flex-shrink-0">
              {comment.name.charAt(0).toUpperCase()}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h4 className="font-bold text-[#2a4b7c]">{comment.name}</h4>
                <span className="text-sm text-[#4b5563]">
                  {formatCommentDate(comment.createdAt)}
                </span>
              </div>
              <p className="text-[#4b5563] leading-relaxed mb-3">
                {comment.content}
              </p>
              <button
                onClick={() => setReplyingTo(isReplying ? null : comment.id)}
                className="text-[#2a4b7c] text-sm font-semibold hover:text-[#1e3a5f] transition-colors flex items-center gap-1"
              >
                <i className="ri-reply-line"></i>
                {isReplying ? "Hủy" : "Trả lời"}
              </button>
            </div>
          </div>
        </div>

        {isReplying && (
          <form
            onSubmit={(e) => handleSubmitReply(comment.id, e)}
            className="mt-4 ml-12 bg-gray-50 rounded-xl p-6 border-2 border-gray-200"
          >
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <input
                type="text"
                placeholder="Tên của bạn *"
                value={replyContent.name}
                onChange={(e) =>
                  setReplyContent({ ...replyContent, name: e.target.value })
                }
                className="px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-[#2a4b7c] focus:outline-none transition-colors"
                required
              />
              <input
                type="email"
                placeholder="Email của bạn *"
                value={replyContent.email}
                onChange={(e) =>
                  setReplyContent({ ...replyContent, email: e.target.value })
                }
                className="px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-[#2a4b7c] focus:outline-none transition-colors"
                required
              />
            </div>
            <textarea
              placeholder="Nội dung trả lời *"
              value={replyContent.content}
              onChange={(e) =>
                setReplyContent({ ...replyContent, content: e.target.value })
              }
              rows={3}
              className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-[#2a4b7c] focus:outline-none transition-colors resize-none mb-4"
              required
            />
            <button
              type="submit"
              className="px-6 py-3 bg-[#2a4b7c] text-white font-semibold rounded-lg hover:bg-[#1e3a5f] transition-all duration-300 hover:scale-105 whitespace-nowrap"
            >
              Gửi trả lời
            </button>
          </form>
        )}

        {hasReplies && (
          <div className="mt-4 ml-12">
            <button
              onClick={() => toggleReplies(comment.id)}
              className="text-[#2a4b7c] text-sm font-semibold hover:text-[#1e3a5f] transition-colors flex items-center gap-2 mb-4"
            >
              <i
                className={`ri-arrow-${isExpanded ? "up" : "down"}-s-line`}
              ></i>
              {isExpanded ? "Ẩn" : "Xem"} {comment.replies.length} phản hồi
            </button>
            {isExpanded && (
              <div className="space-y-4">
                {comment.replies.map((reply) =>
                  renderComment(reply, depth + 1)
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section - Compact */}
      <section className="relative bg-gradient-to-br from-[#2a4b7c] via-[#1e3a5f] to-[#2a4b7c] py-16">
        <div className="max-w-5xl mx-auto px-6">
          {/* Category Badge */}
          <div
            className={`mt-8 mb-4 transition-all duration-700 delay-100 ${
              isVisible
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-4"
            }`}
          >
            <span className="inline-block px-4 py-2 bg-[#00c897] text-white text-sm font-semibold rounded-full">
              {category?.name}
            </span>
          </div>

          {/* Title */}
          <h1
            className={`text-4xl md:text-5xl font-bold text-white mb-6 leading-tight transition-all duration-700 delay-200 ${
              isVisible
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-4"
            }`}
          >
            {currentNews.title}
          </h1>

          {/* Meta Info */}
          <div
            className={`flex flex-wrap items-center gap-6 text-gray-300 transition-all duration-700 delay-300 ${
              isVisible
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-4"
            }`}
          >
            <div className="flex items-center gap-2">
              <i className="ri-user-line text-[#00c897]"></i>
              <span>{currentNews.author}</span>
            </div>
            <div className="flex items-center gap-2">
              <i className="ri-calendar-line text-[#00c897]"></i>
              <span>{formatDate(currentNews.createdAt)}</span>
            </div>
            <div className="flex items-center gap-2">
              <i className="ri-time-line text-[#00c897]"></i>
              <span>{currentNews.readTime}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-6 py-16">
        <div className="grid lg:grid-cols-3 gap-12">
          {/* Left Sidebar - Share & TOC */}
          <div className="lg:col-span-1 order-2 lg:order-1">
            <div className="lg:sticky lg:top-24 space-y-8">
              {/* Share Section */}
              <div
                className={`bg-white rounded-xl border-2 border-gray-100 p-6 transition-all duration-700 ${
                  isVisible
                    ? "opacity-100 translate-y-0"
                    : "opacity-0 translate-y-8"
                }`}
              >
                <h3 className="text-lg font-bold text-[#333] mb-4 flex items-center gap-2">
                  <i className="ri-share-line text-[#00c897]"></i>
                  Chia sẻ bài viết
                </h3>
                <div className="space-y-3">
                  <button
                    onClick={() => handleShare("facebook")}
                    className="w-full flex items-center gap-3 px-4 py-3 bg-[#1877f2] text-white rounded-lg hover:bg-[#0c63d4] transition-all duration-300 hover:scale-105"
                  >
                    <i className="ri-facebook-fill text-xl"></i>
                    <span className="font-medium">Facebook</span>
                  </button>
                  <button
                    onClick={() => handleShare("twitter")}
                    className="w-full flex items-center gap-3 px-4 py-3 bg-[#1da1f2] text-white rounded-lg hover:bg-[#0c8bd9] transition-all duration-300 hover:scale-105"
                  >
                    <i className="ri-twitter-fill text-xl"></i>
                    <span className="font-medium">Twitter</span>
                  </button>
                  <button
                    onClick={() => handleShare("linkedin")}
                    className="w-full flex items-center gap-3 px-4 py-3 bg-[#0077b5] text-white rounded-lg hover:bg-[#005885] transition-all duration-300 hover:scale-105"
                  >
                    <i className="ri-linkedin-fill text-xl"></i>
                    <span className="font-medium">LinkedIn</span>
                  </button>
                  <button
                    onClick={() => handleShare("copy")}
                    className="w-full flex items-center gap-3 px-4 py-3 bg-gray-100 text-[#333] rounded-lg hover:bg-gray-200 transition-all duration-300 hover:scale-105"
                  >
                    <i className="ri-link text-xl"></i>
                    <span className="font-medium">Sao chép link</span>
                  </button>
                </div>
              </div>

              {/* Author Card */}
              <div
                className={`bg-gradient-to-br from-[#2a4b7c]/5 to-[#00c897]/5 rounded-xl border-2 border-gray-100 p-6 transition-all duration-700 delay-200 ${
                  isVisible
                    ? "opacity-100 translate-y-0"
                    : "opacity-0 translate-y-8"
                }`}
              >
                <div className="flex items-center gap-4 mb-4">
                  <img
                    src={`https://readdy.ai/api/search-image?query=professional-vietnamese-author-portrait-$%7BcurrentNews.author.replace%28%2F%5Cs%20%2Fg%2C%20-%29%7D-clean-white-background-high-quality-headshot&width=80&height=80&seq=author-${currentNews._id}&orientation=squarish`}
                    alt={currentNews.author}
                    className="w-16 h-16 rounded-full object-cover object-top border-2 border-[#00c897]"
                  />
                  <div>
                    <h4 className="font-bold text-[#333]">
                      {currentNews.author}
                    </h4>
                    <p className="text-sm text-[#4b5563]">Tác giả</p>
                  </div>
                </div>
                <p className="text-sm text-[#4b5563] leading-relaxed">
                  Chuyên gia công nghệ với nhiều năm kinh nghiệm trong lĩnh vực
                  phát triển phần mềm và chuyển đổi số.
                </p>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-2 order-1 lg:order-2">
            {/* Featured Image */}
            <div
              className={`mb-12 transition-all duration-700 delay-400 ${
                isVisible ? "opacity-100 scale-100" : "opacity-0 scale-95"
              }`}
            >
              <img
                src={currentNews.image}
                alt={currentNews.title}
                className="w-full h-auto rounded-2xl shadow-2xl"
              />
            </div>

            {/* Excerpt */}
            <div
              className={`mb-8 transition-all duration-700 delay-500 ${
                isVisible
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-8"
              }`}
            >
              <p className="text-xl text-[#4b5563] leading-relaxed font-medium border-l-4 border-[#00c897] pl-6 py-2 bg-gray-50 rounded-r-lg">
                {currentNews.excerpt}
              </p>
            </div>

            {/* Tags */}
            <div
              className={`flex flex-wrap gap-2 mb-12 transition-all duration-700 delay-600 ${
                isVisible
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-8"
              }`}
            >
              {currentNews.tags.map((tag, index) => (
                <span
                  key={index}
                  className="px-4 py-2 bg-white border-2 border-gray-200 text-[#4b5563] text-sm rounded-full hover:border-[#00c897] hover:text-[#00c897] transition-all duration-300 cursor-pointer"
                >
                  #{tag}
                </span>
              ))}
            </div>

            {/* Article Content */}
            <div
              className={`prose prose-lg max-w-none transition-all duration-700 delay-700 ${
                isVisible
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-8"
              }`}
              dangerouslySetInnerHTML={{ __html: currentNews.content }}
              style={{
                fontSize: "1.125rem",
                lineHeight: "1.875rem",
                color: "#4b5563",
              }}
            />
          </div>
        </div>
      </div>

      {/* Comments Section */}
      <section className="py-24 bg-gradient-to-b from-white via-gray-50 to-white">
        <div className="max-w-5xl mx-auto px-6">
          <div
            className={`mb-12 transition-all duration-1000 ${
              commentsVisible
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-12"
            }`}
          >
            <h2 className="text-4xl font-bold text-[#2a4b7c] mb-4 flex items-center gap-3">
              <i className="ri-chat-3-line text-[#2a4b7c]"></i>
              Bình luận ({comments.length})
            </h2>
            <p className="text-lg text-[#4b5563]">
              Chia sẻ suy nghĩ của bạn về bài viết này
            </p>
          </div>

          {/* Comment Form */}
          <div
            className={`mb-12 transition-all duration-1000 delay-200 ${
              commentsVisible
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-12"
            }`}
          >
            <form
              onSubmit={handleSubmitComment}
              className="bg-white rounded-2xl border-2 border-gray-200 p-8 shadow-lg"
            >
              <h3 className="text-2xl font-bold text-[#2a4b7c] mb-6">
                Để lại bình luận
              </h3>
              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <input
                  type="text"
                  placeholder="Tên của bạn *"
                  value={newComment.name}
                  onChange={(e) =>
                    setNewComment({ ...newComment, name: e.target.value })
                  }
                  className="px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-[#2a4b7c] focus:outline-none transition-colors"
                  required
                />
                <input
                  type="email"
                  placeholder="Email của bạn *"
                  value={newComment.email}
                  onChange={(e) =>
                    setNewComment({ ...newComment, email: e.target.value })
                  }
                  className="px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-[#2a4b7c] focus:outline-none transition-colors"
                  required
                />
              </div>
              <textarea
                placeholder="Nội dung bình luận *"
                value={newComment.content}
                onChange={(e) =>
                  setNewComment({ ...newComment, content: e.target.value })
                }
                rows={5}
                className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-[#2a4b7c] focus:outline-none transition-colors resize-none mb-4"
                required
              />
              <button
                type="submit"
                className="px-8 py-4 bg-[#2a4b7c] text-white font-semibold rounded-lg hover:bg-[#1e3a5f] transition-all duration-300 hover:scale-105 hover:shadow-xl whitespace-nowrap flex items-center gap-2"
              >
                <i className="ri-send-plane-fill"></i>
                Gửi bình luận
              </button>
            </form>
          </div>

          {/* Comments List */}
          <div
            className={`space-y-6 transition-all duration-1000 delay-400 ${
              commentsVisible
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-12"
            }`}
          >
            {currentComments.map((comment) => renderComment(comment))}
          </div>

          {/* Load More Button */}
          {hasMoreComments && (
            <div
              className={`text-center mt-12 transition-all duration-1000 delay-600 ${
                commentsVisible
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-12"
              }`}
            >
              <button
                onClick={handleLoadMore}
                className="px-8 py-4 bg-white border-2 border-[#2a4b7c] text-[#2a4b7c] font-semibold rounded-lg hover:bg-[#2a4b7c] hover:text-white transition-all duration-300 hover:scale-105 hover:shadow-xl whitespace-nowrap flex items-center gap-2 mx-auto"
              >
                <i className="ri-arrow-down-line"></i>
                Xem thêm bình luận ({comments.length - indexOfLastComment} còn
                lại)
              </button>
            </div>
          )}

          {/* Pagination Info */}
          <div
            className={`text-center mt-8 text-[#4b5563] transition-all duration-1000 delay-700 ${
              commentsVisible
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-12"
            }`}
          >
            Đang hiển thị {indexOfFirstComment + 1} -{" "}
            {Math.min(indexOfLastComment, comments.length)} trong tổng số{" "}
            {comments.length} bình luận
          </div>
        </div>
      </section>

      {/* Related News */}
      {relatedNews.length > 0 && (
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div
              className={`text-center mb-16 transition-all duration-1000 ${
                relatedVisible
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-12"
              }`}
            >
              <h2 className="text-4xl font-bold text-[#333] mb-4">
                Bài Viết Liên Quan
              </h2>
              <p className="text-lg text-[#4b5563]">
                Khám phá thêm các bài viết thú vị khác
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {relatedNews.map((news, index) => (
                <article
                  key={news._id}
                  className={`group bg-white rounded-xl overflow-hidden border-2 border-gray-100 hover:border-[#00c897] transition-all duration-700 hover:shadow-xl cursor-pointer ${
                    relatedVisible
                      ? "opacity-100 translate-y-0"
                      : "opacity-0 translate-y-12"
                  }`}
                  style={{ transitionDelay: `${index * 100}ms` }}
                >
                  <div className="relative h-56 overflow-hidden">
                    <img
                      src={news.image}
                      alt={news.title}
                      className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="px-3 py-1 bg-[#00c897] text-white text-xs font-semibold rounded-full">
                        {news.newCategory?.name || news.newCategoryId?.name}
                      </span>
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-4 text-sm text-[#4b5563] mb-3">
                      <span className="flex items-center gap-1">
                        <i className="ri-user-line"></i>
                        {news.author}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-time-line"></i>
                        {news.readTime}
                      </span>
                    </div>
                    <h3 className="text-xl font-bold text-[#333] mb-3 group-hover:text-[#00c897] transition-colors line-clamp-2">
                      {news.title}
                    </h3>
                    <p className="text-[#4b5563] leading-relaxed mb-4 line-clamp-3">
                      {news.excerpt}
                    </p>
                    <Link
                      to={`/news/${news.slug}`}
                      className="inline-flex items-center gap-2 text-[#00c897] font-semibold hover:gap-3 transition-all duration-300"
                    >
                      Đọc thêm
                      <i className="ri-arrow-right-line"></i>
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Newsletter CTA */}
      <section className="py-24 bg-gradient-to-br from-[#2a4b7c] to-[#1e3a5f] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-[#00c897] rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#00c897] rounded-full blur-3xl"></div>
        </div>
        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <h2 className="text-4xl font-bold text-white mb-6">
            Đăng Ký Nhận Tin Tức Mới Nhất
          </h2>
          <p className="text-xl text-gray-300 mb-10">
            Cập nhật những xu hướng công nghệ và bài viết chất lượng mỗi tuần
          </p>
          <form className="flex flex-col sm:flex-row gap-4 max-w-2xl mx-auto">
            <input
              type="email"
              placeholder="Nhập email của bạn"
              className="flex-1 px-6 py-4 rounded-lg text-[#333] focus:outline-none focus:ring-2 focus:ring-[#00c897]"
            />
            <button
              type="submit"
              className="px-8 py-4 bg-[#00c897] text-white font-semibold rounded-lg hover:bg-[#00b587] transition-all duration-300 whitespace-nowrap hover:scale-105 hover:shadow-xl"
            >
              Đăng Ký Ngay
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
